import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../components/common/Logo';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import toast from 'react-hot-toast';

const SellerLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm();

  const role = 'seller'; // Fixed role for seller login

  const sellerConfig = {
    title: 'Seller Portal',
    subtitle: 'Manage your products and sales',
    gradient: 'linear-gradient(135deg, #F59E0B, #D97706)',
  };

  useEffect(() => {
    // Load saved credentials if remember me was checked
    const savedCredentials = localStorage.getItem(`remember_${role}`);
    if (savedCredentials) {
      const { email, password } = JSON.parse(savedCredentials);
      setValue('email', email);
      setValue('password', password);
      setRememberMe(true);
    }
  }, [role, setValue]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const result = await login({ ...data, role });
      
      if (result.success) {
        // Save credentials if remember me is checked
        if (rememberMe) {
          localStorage.setItem(`remember_${role}`, JSON.stringify({
            email: data.email,
            password: data.password
          }));
        } else {
          localStorage.removeItem(`remember_${role}`);
        }

        toast.success('Login successful!');
        
        // Redirect to seller dashboard
        navigate('/seller');
      }
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password', { state: { role } });
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div 
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'linear-gradient(150deg, #FEF3C7, #FDE68A)'
      }}
    >
      <div 
        style={{
          width: '100%',
          maxWidth: '448px',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: '0 20px 25px -5px rgba(245, 158, 11, 0.1), 0 10px 10px -5px rgba(245, 158, 11, 0.04)',
          border: '1px solid rgba(245, 158, 11, 0.2)',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <Logo />
          </div>
          <h2 
            className="outfit"
            style={{ 
              fontWeight: '700', 
              fontSize: '24px', 
              marginBottom: '8px',
              color: '#92400E'
            }}
          >
            {sellerConfig.title}
          </h2>
          <p 
            style={{ 
              fontSize: '14px',
              fontWeight: '500',
              color: '#78350F'
            }}
          >
            {sellerConfig.subtitle}
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#92400E' }}>Email or Username</label>
            <input
              {...register('email', { 
                required: 'Email or username is required' 
              })}
              type="text"
              placeholder="seller@example.com"
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '12px',
                border: '1px solid #F59E0B',
                background: '#FFFFFF',
                fontSize: '16px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#D97706';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#F59E0B';
              }}
            />
            {errors.email && (
              <p style={{ color: '#DC2626', fontSize: '12px', marginTop: '4px' }}>{errors.email.message}</p>
            )}
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#92400E' }}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                {...register('password', { 
                  required: 'Password is required' 
                })}
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  paddingRight: '44px',
                  borderRadius: '12px',
                  border: '1px solid #F59E0B',
                  background: '#FFFFFF',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#D97706';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#F59E0B';
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#92400E',
                  cursor: 'pointer'
                }}
              >
                <Icon name={showPassword ? 'eye' : 'eye'} size={16} />
              </button>
            </div>
            {errors.password && (
              <p style={{ color: '#DC2626', fontSize: '12px', marginTop: '4px' }}>{errors.password.message}</p>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#78350F' }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ accentColor: '#F59E0B', width: '14px', height: '14px' }}
              />
              Remember me
            </label>
            <button
              type="button"
              onClick={handleForgotPassword}
              style={{
                fontSize: '14px',
                fontWeight: '700',
                transition: 'colors 0.2s',
                color: '#D97706',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Forgot?
            </button>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={loading}
            style={{ 
              width: '100%', 
              padding: '12px',
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              border: 'none'
            }}
          >
            Sign In
          </Button>
        </form>

        {/* Register Link */}
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <p style={{ color: '#78350F', fontSize: '13px' }}>
            Want to become a seller?{' '}
            <button
              onClick={() => navigate('/contact')}
              style={{
                fontWeight: '700',
                transition: 'colors 0.2s',
                color: '#D97706',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Apply Now
            </button>
          </p>
        </div>

        {/* Back Button */}
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <button
            onClick={handleBack}
            style={{
              fontSize: '14px',
              fontWeight: '500',
              transition: 'colors 0.2s',
              color: '#78350F',
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default SellerLogin;
